import { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Skeleton, Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { AlertItem, AlertSeverity } from '../api/types';
import { AlertRow, ALERT_TYPE_LABELS } from './AlertRow';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { alertSeverityColor } from '../utils/colors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AlertsPanelProps {
    alerts: AlertItem[];
    loading: boolean;
    error?: string | null;
    markets: Array<{ condition_id: string; question: string }>;
    outcomeColorMap?: Map<string, string>;
    onSelectMarket?: (conditionId: string) => void;
    alertTypes?: Set<string>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_ORDER: AlertSeverity[] = ['critical', 'warning', 'info', 'log'];

// ---------------------------------------------------------------------------
// AlertsPanel
// ---------------------------------------------------------------------------

export function AlertsPanel({
    alerts,
    loading,
    error,
    markets,
    outcomeColorMap,
    onSelectMarket,
    alertTypes,
}: AlertsPanelProps) {
    const theme = useTheme();

    // --- Expand state (accordion — one at a time) ---
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const toggleExpand = useCallback((id: string) => {
        setExpandedId(prev => (prev === id ? null : id));
    }, []);

    // --- Filter state ---
    const [severityFilter, setSeverityFilter] = useState<Set<AlertSeverity>>(new Set());
    const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
    const [marketFilter, setMarketFilter] = useState<string | null>(null);

    // --- Pre-filter by alertTypes prop (future per-sidebar scoping) ---
    const scopedAlerts = useMemo(() => {
        if (!alertTypes) return alerts;
        return alerts.filter(a => alertTypes.has(a.alert_type));
    }, [alerts, alertTypes]);

    // --- Derived filter options ---
    const presentSeverities = useMemo(() => {
        const set = new Set<AlertSeverity>();
        for (const a of scopedAlerts) set.add(a.severity);
        return SEVERITY_ORDER.filter(s => set.has(s));
    }, [scopedAlerts]);

    const presentTypes = useMemo(() => {
        const set = new Set<string>();
        for (const a of scopedAlerts) set.add(a.alert_type);
        return [...set].sort();
    }, [scopedAlerts]);

    const presentMarketIds = useMemo(() => {
        const set = new Set<string>();
        for (const a of scopedAlerts) set.add(a.market_id);
        return [...set];
    }, [scopedAlerts]);

    const marketLookup = useMemo(() => {
        const map = new Map<string, string>();
        for (const m of markets) map.set(m.condition_id, m.question);
        return map;
    }, [markets]);

    const isMultiMarket = presentMarketIds.length > 1;

    // --- Apply filters ---
    const filtered = useMemo(() => {
        return scopedAlerts.filter(a => {
            if (severityFilter.size > 0 && !severityFilter.has(a.severity)) return false;
            if (typeFilter.size > 0 && !typeFilter.has(a.alert_type)) return false;
            if (marketFilter && a.market_id !== marketFilter) return false;
            return true;
        });
    }, [scopedAlerts, severityFilter, typeFilter, marketFilter]);

    // --- Severity summary ---
    const severityCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const a of scopedAlerts) {
            counts[a.severity] = (counts[a.severity] ?? 0) + 1;
        }
        return counts;
    }, [scopedAlerts]);

    // --- Toggle helpers ---
    const toggleSeverity = useCallback((s: AlertSeverity) => {
        setSeverityFilter(prev => {
            const next = new Set(prev);
            if (next.has(s)) next.delete(s);
            else next.add(s);
            return next;
        });
    }, []);

    const toggleType = useCallback((t: string) => {
        setTypeFilter(prev => {
            const next = new Set(prev);
            if (next.has(t)) next.delete(t);
            else next.add(t);
            return next;
        });
    }, []);

    const toggleMarket = useCallback((id: string) => {
        setMarketFilter(prev => (prev === id ? null : id));
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            color: 'text.disabled',
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Alerts
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', ml: 'auto' }}>
                        {scopedAlerts.length} total
                        {SEVERITY_ORDER.filter(s => severityCounts[s]).map(s => {
                            const count = severityCounts[s];
                            return count ? ` · ${count} ${s}` : '';
                        }).join('')}
                    </Typography>
                </Box>

                {/* Severity filter chips */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: presentTypes.length > 1 ? 0.5 : 0 }}>
                    {presentSeverities.map(s => {
                        const active = severityFilter.has(s);
                        const chipColor = alertSeverityColor(s, theme.palette.text.secondary);
                        return (
                            <Chip
                                key={s}
                                label={s}
                                size="small"
                                onClick={() => toggleSeverity(s)}
                                sx={{
                                    height: 20,
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                    bgcolor: active ? alpha(chipColor, 0.15) : 'transparent',
                                    color: active ? chipColor : 'text.disabled',
                                    border: 1,
                                    borderColor: active ? alpha(chipColor, 0.3) : 'divider',
                                    '&:hover': { bgcolor: alpha(chipColor, 0.1) },
                                }}
                            />
                        );
                    })}
                </Box>

                {/* Type filter chips */}
                {presentTypes.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: isMultiMarket ? 0.5 : 0 }}>
                        {presentTypes.map(t => {
                            const active = typeFilter.has(t);
                            return (
                                <Chip
                                    key={t}
                                    label={ALERT_TYPE_LABELS[t] ?? t}
                                    size="small"
                                    onClick={() => toggleType(t)}
                                    sx={{
                                        height: 20,
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                                        color: active ? 'primary.main' : 'text.disabled',
                                        border: 1,
                                        borderColor: active ? alpha(theme.palette.primary.main, 0.3) : 'divider',
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                {/* Market filter chips (multi-market only) */}
                {isMultiMarket && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {presentMarketIds.map(id => {
                            const active = marketFilter === id;
                            const question = marketLookup.get(id) ?? id.slice(0, 10);
                            const chipColor = outcomeColorMap?.get(id) ?? theme.palette.primary.main;
                            return (
                                <Chip
                                    key={id}
                                    label={question.length > 30 ? question.slice(0, 30) + '…' : question}
                                    size="small"
                                    onClick={() => toggleMarket(id)}
                                    sx={{
                                        height: 20,
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        maxWidth: 200,
                                        bgcolor: active ? alpha(chipColor, 0.12) : 'transparent',
                                        color: active ? chipColor : 'text.disabled',
                                        border: 1,
                                        borderColor: active ? alpha(chipColor, 0.3) : 'divider',
                                        '&:hover': { bgcolor: alpha(chipColor, 0.08) },
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Alert list */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', ...hoverScrollbar }}>
                {loading && scopedAlerts.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 0.5 }} />
                        ))}
                    </Box>
                ) : error ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '12px', color: 'error.main' }}>
                            Failed to load alerts: {error}
                        </Typography>
                    </Box>
                ) : filtered.length > 0 ? (
                    filtered.map(alert => (
                        <AlertRow
                            key={alert.id}
                            alert={alert}
                            marketQuestion={marketLookup.get(alert.market_id) ?? alert.market_id.slice(0, 10)}
                            marketColor={outcomeColorMap?.get(alert.market_id)}
                            onClickMarket={onSelectMarket ? () => onSelectMarket(alert.market_id) : undefined}
                            expanded={expandedId === alert.id}
                            onToggleExpand={() => toggleExpand(alert.id)}
                        />
                    ))
                ) : (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                            {scopedAlerts.length === 0
                                ? 'No alerts for this event'
                                : 'No alerts match the current filters'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
